import { Link, useLocation, useRouter } from "@tanstack/react-router";
import { isToday, isYesterday, subMonths, subWeeks } from "date-fns";
import {
  Calendar,
  ChevronRight,
  Loader2,
  MoreHorizontal,
  PackageOpen,
  Trash2,
  Download,
} from "lucide-react";

import { Button, buttonVariants } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSseRevalidation } from "@/hooks/use-sse";
import { cn } from "@/lib/utils";
import { Batch, BatchStatus } from "@/types/dashboard.types";
import { toast } from "sonner";
import { useDeleteConversion } from "../queries/conversion.delete.queries";
import { useHistory } from "@/providers/history.provider";
import { useExportBatchCsv } from "../queries/conversions.export.queries";

type GroupedBatches = {
  today: Batch[];
  yesterday: Batch[];
  lastWeek: Batch[];
  lastMonth: Batch[];
  older: Batch[];
};

function HistoryBadge({ status }: { status?: BatchStatus | null }) {
  if (!status) {
    return <div />;
  } else if (status === "CREATED") {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <span className="relative flex size-2">
            <span className="relative inline-flex size-2 rounded-full bg-sky-500"></span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs">
          <p>Conversion pending</p>
        </TooltipContent>
      </Tooltip>
    );
  } else if (status === "REVIEW_REQUIRED") {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <span className="relative flex size-2">
            <span className="relative inline-flex size-2 rounded-full bg-yellow-500"></span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs ">
          <p>Conversion requires review</p>
        </TooltipContent>
      </Tooltip>
    );
  } else if (status === "FAILED") {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <div className="size-2 rounded-full bg-red-500" />
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs">
          <p>Contains failed conversions</p>
        </TooltipContent>
      </Tooltip>
    );
  } else if (status === "REVIEWED") {
    return <div />;
  }
}

function HistoryItem({ batch, pathname }: { batch: Batch; pathname: string }) {
  const router = useRouter();

  const isActive = pathname === `/dashboard/conversion-history/${batch.id}`;

  // temp fix
  const showAction = false;

  const { mutateAsync: deleteConversion, isPending } = useDeleteConversion();
  const { mutateAsync: exportBatchCsv, isPending: isExporting } = useExportBatchCsv();

  async function handleDeleteConversion() {
    try {
      await deleteConversion({ id: batch.id });
      toast.success("Conversion deleted successfully");
      router.navigate({ to: "/dashboard" });
    } catch (error) {
      toast.error("Failed to delete conversion");
    }
  }

  async function handleDownloadCsv() {
    try {
      await exportBatchCsv(batch.id);
    } catch (error) {
      console.error("Failed to download CSV:", error);
    }
  }

  return (
    <SidebarMenu key={batch.id}>
      <SidebarMenuItem>
        <Collapsible asChild className="group/collapsible">
          <SidebarMenuItem>
            <CollapsibleTrigger
              asChild
              onClick={(e) => {
                if (batch.status === "CREATED") {
                  e.preventDefault();
                  e.stopPropagation();
                  toast.info("Conversion is pending", {
                    description: "You can't open a pending conversion",
                  });
                  return;
                }
              }}
            >
              <SidebarMenuButton isActive={isActive} tooltip={batch.name}>
                <div className="flex items-center justify-between w-full gap-2">
                  <HistoryBadge status={batch.status} />
                  <p
                    className={cn({
                      "font-semibold": isActive,
                      "cursor-not-allowed": batch.status === "CREATED",
                      "text-muted-foreground": batch.status === "CREATED",
                    })}
                  >
                    {batch.name.slice(0, 19)}...
                  </p>
                  <ChevronRight
                    className={cn(
                      "ml-auto size-4 transition-transform text-muted-foreground duration-200 group-data-[state=open]/collapsible:rotate-90",
                      {
                        invisible: batch.status === "CREATED",
                      }
                    )}
                  />
                </div>
              </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarMenuSub>
                {batch.documents.map((subItem) => (
                  <SidebarMenuSubItem key={subItem.name}>
                    <SidebarMenuSubButton asChild>
                      <Link
                        params={{ id: batch.id }}
                        to={`/dashboard/conversion-history/$id`}
                        className="w-full"
                      >
                        <span>{subItem.name}</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      </SidebarMenuItem>
      {showAction && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuAction>
              <MoreHorizontal />
            </SidebarMenuAction>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="center">
            {batch.status === "COMPLETED" && (
              <DropdownMenuItem
                onSelect={(e) => e.preventDefault()}
                disabled={isExporting}
                onClick={handleDownloadCsv}
                className="flex items-center gap-2"
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {isExporting ? "Generating..." : "Download CSV"}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onSelect={(e) => e.preventDefault()}
              disabled={isPending}
              onClick={handleDeleteConversion}
              className={cn(
                buttonVariants({
                  variant: "destructive",
                  className: "hover:bg-destructive/10",
                })
              )}
            >
              <Trash2 className="h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </SidebarMenu>
  );
}

function GroupSection({
  label,
  batches,
  pathname,
}: {
  label: string;
  batches: Batch[];
  pathname: string;
}) {
  if (batches.length === 0) return null;

  return (
    <div>
      <div className="px-2 flex bg-primary/0 items-center gap-2 border border-primary/20 py-2 text-sm text-primary rounded-md">
        <Calendar className="size-4" />
        {label}
      </div>
      <div className="mt-1.5 space-y-1.5">
        {batches.map((batch) => (
          <HistoryItem key={batch.id} batch={batch} pathname={pathname} />
        ))}
      </div>
    </div>
  );
}

export function SidebarHistory() {
  useSseRevalidation();

  const {
    history: data,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useHistory();

  const allConversions = data?.pages.flatMap((page) => page.batches) ?? [];

  const { pathname } = useLocation();

  const groupChatsByDate = (
    chats: Batch[] | undefined
  ): GroupedBatches | undefined => {
    const now = new Date();
    const oneWeekAgo = subWeeks(now, 1);
    const oneMonthAgo = subMonths(now, 1);

    return chats?.reduce(
      (groups, chat) => {
        const chatDate = new Date(chat.createdAt);

        if (isToday(chatDate)) {
          groups.today.push(chat);
        } else if (isYesterday(chatDate)) {
          groups.yesterday.push(chat);
        } else if (chatDate > oneWeekAgo) {
          groups.lastWeek.push(chat);
        } else if (chatDate > oneMonthAgo) {
          groups.lastMonth.push(chat);
        } else {
          groups.older.push(chat);
        }

        return groups;
      },
      {
        today: [],
        yesterday: [],
        lastWeek: [],
        lastMonth: [],
        older: [],
      } as GroupedBatches
    );
  };

  const groupedChats = groupChatsByDate(allConversions);

  return (
    <div id="history-sidebar-items">
      <div className="space-y-3">
        {!data && isPending && (
          <div>
            <Loader2 className="size-6 animate-spin mx-auto mt-5" />
          </div>
        )}

        {allConversions.length === 0 && !isPending && (
          <div className="my-2">
            <p className="text-xs flex items-center justify-center gap-2 text-muted-foreground">
              <PackageOpen className="size-4 text-yellow-600" />
              No conversions found
            </p>
          </div>
        )}

        {groupedChats && (
          <div className="space-y-3">
            <GroupSection
              label="Today"
              batches={groupedChats.today}
              pathname={pathname}
            />
            <GroupSection
              label="Yesterday"
              batches={groupedChats.yesterday}
              pathname={pathname}
            />
            <GroupSection
              label="Last Week"
              batches={groupedChats.lastWeek}
              pathname={pathname}
            />
            <GroupSection
              label="Last 30 Days"
              batches={groupedChats.lastMonth}
              pathname={pathname}
            />
            <GroupSection
              label="Older"
              batches={groupedChats.older}
              pathname={pathname}
            />
          </div>
        )}

        {hasNextPage && (
          <div className="pt-2">
            <Button
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
              variant="outline"
              className="w-full"
            >
              {isFetchingNextPage ? (
                <Loader2 className="size-4 animate-spin mx-auto" />
              ) : (
                "Load more"
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
